import { inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import {
  type AffiliateExportRow,
  type AffiliateRepository,
  type RoleRepository,
  type UserRepository,
  type WorkspaceRepository,
} from '@telespot/domain';
import { EMPTY, Observable, of, from, Subject, merge } from 'rxjs';
import { catchError, switchMap, tap, filter, mergeMap, map } from 'rxjs/operators';

import { EntityAdapter, EntitySelectors } from '@ngrx/entity/src/models';
import {
  AddUser,
  GetUserRoles,
  GrantRoles,
  ListUsers,
  RemoveMemberships,
  RevokeRoles,
} from '@telespot/authentication/use-cases';
import { PaginatedStore, PaginatedStateLoadingMode, PaginatedStateModel } from '@shared/ui';
import { RoleData } from '@telespot/authentication/ui';
import { UserClient } from './users.client';
import {
  ExportOrganizationAffiliates,
  ListUserMemberships,
  ListUserMembershipsUseCase,
} from '@telespot/management/use-cases';

export interface AffiliatedUserData {
  id: string;
  affiliationId: string | undefined;
  username: string;
  emailVerified: boolean;
  avatarUrl: string | undefined;
  affiliationDate: Date;
  roles?: RoleData[];
  numWorkspaces?: number;
}

export interface AffiliatedUsersStoreState extends PaginatedStateModel<AffiliatedUserData> {
  // Current organization
  organization: { id: string; maxUsers: number } | undefined;

  // Active user
  selectedUser?: AffiliatedUserData;
  updatedRoles: EntityState<RoleData>;
  addedToOrganization: boolean;
  rolesUpdated: boolean;
  hasChanges: boolean;
  isDemoted: boolean;

  // User creation
  userCreated: boolean;
  organizationLimitReached: boolean;
  existingUserAssignedToOrganization: boolean;
  userForm: {
    email?: string;
    username: string;
    password: string;
    registerWithoutEmail: boolean;
    // When true, the form only adds an ALREADY-EXISTING user (by username or
    // email) to the organization; roles are inherited and not edited here.
    addExistingUser: boolean;
    isValid: boolean;
  };
}

export enum UserCreatedStatus {
  CREATED = 'CREATED',
  ASSIGNED_TO_ORGANIZATION = 'ASSIGNED_TO_ORGANIZATION',
  NOT_CREATED = 'NOT_CREATED',
}

export const USER_REPOSITORY = new InjectionToken<UserRepository>('USER_REPOSITORY');
export const USERS_ROLES_REPOSITORY = new InjectionToken<RoleRepository>('USERS_ROLES_REPOSITORY');
export const AFFILIATE_REPOSITORY = new InjectionToken<AffiliateRepository>('AFFILIATE_REPOSITORY');
export const USERS_WORKSPACE_REPOSITORY = new InjectionToken<WorkspaceRepository>('USERS_WORKSPACE_REPOSITORY');
export const LIST_USER_MEMBERSHIPS_USE_CASE = new InjectionToken<ListUserMembershipsUseCase>(
  'LIST_USER_MEMBERSHIPS_USE_CASE'
);
export const USER_CLIENT = new InjectionToken<UserClient>('USER_CLIENT');
export const PAGE_SIZE = 10;
const MAX_CONCURRENCY = 2;

@Injectable()
export class AffiliatedUsersStore
  extends PaginatedStore<AffiliatedUserData, AffiliatedUsersStoreState>
  implements OnDestroy
{
  public readonly rolesAdapter: EntityAdapter<RoleData>;

  // Cached selectors for performance
  private readonly rolesSelectors: EntitySelectors<RoleData, EntityState<RoleData>>;

  // Subjects for triggering cascading effects
  private readonly selectUserSucceeded$ = new Subject<AffiliatedUserData>();
  private readonly updateUserTrigger$ = new Subject<void>();

  private readonly userRepository = inject<UserRepository>(USER_REPOSITORY);
  private readonly rolesRepository = inject<RoleRepository>(USERS_ROLES_REPOSITORY);
  private readonly affiliateRepository = inject<AffiliateRepository>(AFFILIATE_REPOSITORY);
  private readonly workspaceRepository = inject<WorkspaceRepository>(USERS_WORKSPACE_REPOSITORY);
  private readonly listUserMembershipsUseCase = inject<ListUserMembershipsUseCase>(LIST_USER_MEMBERSHIPS_USE_CASE);
  private readonly userClient = inject<UserClient>(USER_CLIENT);

  constructor() {
    // Entity Adapters for roles and memberships
    const tempRolesAdapter = createEntityAdapter<RoleData>({
      selectId: (role) => role.name,
    });
    // Call super with user-specific state
    super(
      {
        organization: undefined,
        selectedUser: undefined,
        updatedRoles: tempRolesAdapter.getInitialState(),
        addedToOrganization: true,
        rolesUpdated: true,
        hasChanges: false,
        isDemoted: false,
        organizationLimitReached: false,
        userCreated: true,
        existingUserAssignedToOrganization: false,
        userForm: {
          email: undefined,
          username: '',
          password: '',
          registerWithoutEmail: false,
          addExistingUser: false,
          isValid: false,
        },
      },
      {
        initialPageSize: PAGE_SIZE,
        maxConcurrentCountQueries: MAX_CONCURRENCY,
        loadingMode: PaginatedStateLoadingMode.PAGINATED,
      }
    );

    // Keep references to the adapters
    this.rolesAdapter = tempRolesAdapter;

    // Initialize selectors
    this.rolesSelectors = this.rolesAdapter.getSelectors();

    // Initialize cascading effects
    this.initializeCascadingEffects();
  }

  // Selectors - Aliases for backwards compatibility
  public readonly users$ = this.items$;
  public readonly usersEntities$ = this.select((state) => state.items.entities);
  public readonly nextUserToken$ = this.nextToken$;
  public readonly previousUserTokens$ = this.previousTokens$;
  public readonly totalUserCount$ = this.totalCount$;
  public readonly usersLoaded$ = this.itemsLoaded$;
  public readonly loadingUsers$ = this.loading$;
  public readonly deletingUser$ = this.deleting$;
  public readonly noUsers$ = this.noItems$;

  // User-specific selectors
  public readonly organization$ = this.select((state) => state.organization);
  public readonly selectedUser$ = this.select((state) => state.selectedUser);
  public readonly userLoadedForEdit$ = this.select((state) => state.selectedUser !== undefined);
  public readonly selectedUserRoles$ = this.select((state) => (state.selectedUser ? state.selectedUser.roles : []));
  public readonly updatedRoles$ = this.select((state) => this.rolesSelectors.selectAll(state.updatedRoles));
  public readonly existingUserAssignedToOrganization$ = this.select(
    (state) => state.existingUserAssignedToOrganization
  );
  public readonly hasChanges$ = this.select((state) => state.hasChanges);
  public readonly isDemoted$ = this.select((state) => state.isDemoted);
  public readonly rolesUpdated$ = this.select((state) => state.rolesUpdated);
  public readonly addedToOrganization$ = this.select((state) => state.addedToOrganization);
  public readonly userCreated$ = this.select((state) => state.userCreated);
  public readonly organizationLimitReached$ = this.select((state) => state.organizationLimitReached);

  // User form selectors
  public readonly userForm$ = this.select((state) => state.userForm);
  public readonly userFormEmail$ = this.select((state) => state.userForm.email);
  public readonly userFormUsername$ = this.select((state) => state.userForm.username);
  public readonly userFormPassword$ = this.select((state) => state.userForm.password);
  public readonly registerWithoutEmail$ = this.select((state) => state.userForm.registerWithoutEmail);
  public readonly addExistingUser$ = this.select((state) => state.userForm.addExistingUser);
  public readonly userFormIsValid$ = this.select((state) => state.userForm.isValid);

  private readonly mustCheckOrganizationLimit$ = this.select(
    this.userCreated$,
    this.totalUserCount$,
    (userCreated, totalUserCount) => !userCreated && totalUserCount > 0
  );

  public readonly setOrganization = this.updater((state, organization: { id: string; maxUsers: number }) => ({
    ...state,
    organization,
  }));

  private readonly setOrganizationLimitReached = this.updater((state, organizationLimitReached: boolean) => {
    return { ...state, organizationLimitReached };
  });

  private readonly setSelectedUser = this.updater((state, user: AffiliatedUserData) => {
    return {
      ...state,
      selectedUser: user,
      updatedRoles: this.rolesAdapter.setAll(user.roles || [], state.updatedRoles),
    };
  });

  private readonly addUserWithAffiliation = this.updater(
    (state, { user, userExisted }: { user: AffiliatedUserData; userExisted: boolean }) => {
      return {
        ...state,
        items: this.itemsAdapter.addOne(user, state.items),
        addedToOrganization: true,
        userCreated: true,
        selectedUser: user,
        existingUserAssignedToOrganization: userExisted ? true : state.existingUserAssignedToOrganization,
      };
    }
  );

  private readonly updateSelectedUserRoles = this.updater(
    (state, { userId, roles }: { userId: string; roles: RoleData[] }) => {
      return {
        ...state,
        updatedRoles:
          state.selectedUser?.id === userId ? this.rolesAdapter.setAll(roles, state.updatedRoles) : state.updatedRoles,
      };
    }
  );

  public readonly resetSelectedUserState = this.updater((state) => ({
    ...state,
    selectedUser: undefined,
    updatedRoles: this.rolesAdapter.removeAll(state.updatedRoles),
    hasChanges: false,
  }));

  public readonly updateUserInList = this.updater(
    (state, { userId, changes }: { userId: string; changes: Partial<AffiliatedUserData> }) => {
      return {
        ...state,
        rolesUpdated: true,
        items: this.itemsAdapter.updateOne({ id: userId, changes }, state.items),
      };
    }
  );

  // User form updaters
  private readonly updateUserFormField = this.updater(
    (state, { field, value }: { field: keyof AffiliatedUsersStoreState['userForm']; value: string | boolean }) => ({
      ...state,
      userForm: {
        ...state.userForm,
        [field]: value,
        isValid: this.validateUserForm({ ...state.userForm, [field]: value }),
      },
    })
  );

  private readonly resetUserForm = this.updater((state) => ({
    ...state,
    userCreated: false,
    userForm: {
      email: undefined,
      username: '',
      password: '',
      registerWithoutEmail: false,
      addExistingUser: false,
      isValid: false,
    },
  }));

  public readonly toggleRegisterWithoutEmail = this.updater((state, registerWithoutEmail: boolean) => {
    const newFormState = {
      ...state.userForm,
      registerWithoutEmail,
      email: registerWithoutEmail ? undefined : state.userForm.email,
      password: registerWithoutEmail ? this.autogeneratePassword() : state.userForm.password,
    };

    return {
      ...state,
      userForm: {
        ...newFormState,
        isValid: this.validateUserForm(newFormState),
      },
    };
  });

  public readonly toggleAddExistingUser = this.updater((state, addExistingUser: boolean) => {
    const newFormState = {
      ...state.userForm,
      addExistingUser,
      // Adding an existing user is mutually exclusive with registering a new one.
      registerWithoutEmail: false,
      email: undefined,
      password: '',
      username: '',
    };

    return {
      ...state,
      // Existing users keep their global roles, so any pending role edits are dropped.
      updatedRoles: this.rolesAdapter.getInitialState(),
      // Affiliating an existing user is itself the change, so saving is allowed
      // without any role edit (which otherwise gates `hasChanges`).
      hasChanges: addExistingUser,
      isDemoted: false,
      userForm: {
        ...newFormState,
        isValid: this.validateUserForm(newFormState),
      },
    };
  });

  public readonly setFormUsername = this.updater((state, username: string) => ({
    ...state,
    userForm: {
      ...state.userForm,
      username,
      isValid: this.validateUserForm({ ...state.userForm, username }),
    },
  }));

  public readonly setFormPassword = this.updater((state, password: string) => ({
    ...state,
    userForm: {
      ...state.userForm,
      password,
      isValid: this.validateUserForm({ ...state.userForm, password }),
    },
  }));

  public readonly markRoleAsGranted = this.updater((state, role: RoleData) => {
    const activeRoleIds = state.selectedUser?.roles?.map((r) => r.id) || [];
    const updatedRoleIds = this.rolesSelectors.selectAll(state.updatedRoles).map((r) => r.id);
    const futureUpdatedRoleIds = [...updatedRoleIds, role.id];
    const demoted = activeRoleIds.some((roleId: string) => !futureUpdatedRoleIds.includes(roleId));
    const hasChanges = futureUpdatedRoleIds.length !== activeRoleIds.length || demoted;

    return {
      ...state,
      updatedRoles: this.rolesAdapter.addOne(role as RoleData, state.updatedRoles),
      hasChanges: hasChanges,
      isDemoted: demoted,
      rolesUpdated: hasChanges,
    };
  });

  public readonly markRoleAsRevoked = this.updater((state, role: RoleData) => {
    const activeRoleIds = state.selectedUser?.roles?.map((r) => r.id) || [];
    const updatedRoleIds = this.rolesSelectors.selectAll(state.updatedRoles).map((r) => r.id);
    const futureUpdatedRoleIds = updatedRoleIds.filter((roleId: string) => roleId !== role.id);
    const demoted = activeRoleIds.some((roleId: string) => !futureUpdatedRoleIds.includes(roleId));
    const hasChanges = futureUpdatedRoleIds.length !== activeRoleIds.length || demoted;

    return {
      ...state,
      updatedRoles: this.rolesAdapter.removeOne(role.name, state.updatedRoles),
      hasChanges: hasChanges,
      isDemoted: demoted,
      rolesUpdated: hasChanges,
    };
  });

  // Backwards-compatible effect aliases
  public readonly loadMoreUsers = this.loadMoreItems;
  public readonly countOrgUsers = this.countItems;
  private readonly listOrganizationUsers = this.effect(
    (organizationId$: Observable<{ id: string; maxUsers: number } | undefined>) =>
      organizationId$.pipe(
        filter((organization) => organization !== undefined),
        tap(() => this.initializeItems())
      )
  );

  private readonly checkOrganizationLimit = this.effect((mustCheckOrganizationLimit$: Observable<boolean>) =>
    mustCheckOrganizationLimit$.pipe(
      filter((check) => check),
      concatLatestFrom(() => [this.organization$, this.totalUserCount$]),
      switchMap(([, organization, currentUserCount]) => {
        if (organization && organization.maxUsers !== -1 && currentUserCount >= organization.maxUsers) {
          this.setOrganizationLimitReached(true);
        }
        return EMPTY;
      })
    )
  );

  public readonly createAndAffiliateUser = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.userForm$, this.organization$]),
      switchMap(([, formState, organization]) => {
        return from(
          this.userClient.send(
            new AddUser({
              emailOrUsername: formState.username.trim(),
              password: formState.password,
              organizationId: organization?.id ?? '',
            })
          )
        ).pipe(
          tap((affiliateInfo) => {
            const user = {
              id: affiliateInfo.userId,
              username: affiliateInfo.username,
              email: affiliateInfo.userEmail,
              affiliationId: affiliateInfo.affiliationId,
              emailVerified: false,
              avatarUrl: '',
              affiliationDate: new Date(),
            };

            this.addUserWithAffiliation({
              user,
              // If the user already existed, the UI must notify this
              userExisted: affiliateInfo.userExisted,
            });

            // Trigger role and workspace sync
            this.updateUserTrigger$.next();
          }),
          this.handleError()
        );
      })
    )
  );

  public readonly selectUser = this.effect(
    (action$: Observable<{ userId: string; organizationId: string; maxUsers: number }>) =>
      action$.pipe(
        tap(({ organizationId, maxUsers }) => {
          this.setOrganization({ id: organizationId, maxUsers });
        }),
        concatLatestFrom(() => [this.usersEntities$]),
        filter(([{ userId }]) => !!userId && userId !== ''),
        switchMap(([{ userId }, entities]) => {
          const user = entities[userId];
          if (user) {
            this.resetSelectedUserState();
            this.setSelectedUser(user);
            this.selectUserSucceeded$.next(user);
            return of(user);
          } else {
            // Fetch user if not in local state
            return from(this.getUser(userId)).pipe(
              tap((fetchedUser) => {
                this.resetSelectedUserState();
                this.setSelectedUser(fetchedUser);
                this.selectUserSucceeded$.next(fetchedUser);
              }),
              this.handleError()
            );
          }
        })
      )
  );

  public readonly removeUserFromOrganization = this.deleteItem;

  private readonly getSelectedUserRoles = this.effect((userId$: Observable<string>) =>
    userId$.pipe(
      mergeMap((userId) => {
        return this.retrieveRoles(of(userId)).pipe(
          tap(({ roles }: { roles: RoleData[] }) => {
            this.updateSelectedUserRoles({ userId, roles });
          }),
          this.handleError()
        );
      }, MAX_CONCURRENCY)
    )
  );

  private readonly onSelectUserSucceeded = this.effect((user$: Observable<AffiliatedUserData>) =>
    user$.pipe(
      filter((user) => !user?.roles?.length),
      tap((user) => {
        this.getSelectedUserRoles(of((user as AffiliatedUserData).id));
      })
    )
  );

  private readonly onUpdateUser = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.selectedUser$]),
      filter(([, selectedUser]) => selectedUser !== undefined),
      concatLatestFrom(() => [this.updatedRoles$]),
      switchMap(([[, selectedUser], selectedRoles]) => {
        const userId = selectedUser?.id ?? '';
        // Roles the user had but that are no longer selected must be revoked,
        // otherwise a demotion (removing a role) would never persist.
        const revokedRoles = (selectedUser?.roles ?? []).filter(
          (role) => !selectedRoles.some((selected) => selected.id === role.id)
        );

        const grantRoles$ = from(
          this.userClient.send(new GrantRoles({ userId, roles: selectedRoles.map((r) => r.name) }))
        );

        const syncRoles$ = revokedRoles.length
          ? grantRoles$.pipe(
              switchMap(() =>
                from(this.userClient.send(new RevokeRoles({ userId, roles: revokedRoles.map((r) => r.name) })))
              )
            )
          : grantRoles$;

        return syncRoles$.pipe(
          tap(() => {
            // Update user in the list with new roles
            this.updateUserInList({ userId, changes: { roles: selectedRoles } });
          }),
          this.handleError()
        );
      })
    )
  );

  public readonly setFormEmail = this.effect((email$: Observable<string>) =>
    email$.pipe(
      tap((email: string) => {
        this.updateUserFormField({ field: 'email', value: email });
        // Auto-update username when email changes and not in registerWithoutEmail mode
        if (!this.get((state) => state.userForm.registerWithoutEmail)) {
          this.updateUserFormField({ field: 'username', value: email });
        }
      })
    )
  );

  public readonly syncSelectedUser = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(tap(() => this.updateUserTrigger$.next()))
  );

  public readonly initializeForm = this.effect((input$: Observable<{ organizationId: string; maxUsers: number }>) =>
    input$.pipe(
      tap(({ organizationId, maxUsers }) => {
        this.setOrganization({ id: organizationId, maxUsers });
        this.countOrgUsers();
        this.resetUserForm();
      })
    )
  );

  private autogeneratePassword(length = 12): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const buffer = new Uint8Array(length);
    crypto.getRandomValues(buffer);
    return Array.from(buffer, (byte) => charset[byte % charset.length]).join('');
  }

  private validateUserForm(formState: AffiliatedUsersStoreState['userForm']): boolean {
    if (formState.addExistingUser) {
      // A single field holds either a username or an email; the backend resolves it.
      return !!formState.username?.trim();
    }
    if (formState.registerWithoutEmail) {
      return !!(formState.username?.trim() && formState.password?.trim());
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return !!(formState.email?.trim() && emailRegex.test(formState.email) && formState.username?.trim());
    }
  }

  public getAffiliatesForExport(organizationId: string): Promise<AffiliateExportRow[]> {
    return this.userClient.send(new ExportOrganizationAffiliates({ organizationId })) as Promise<AffiliateExportRow[]>;
  }

  // Implement abstract methods from PaginatedStore
  protected async countRepository(searchTerm?: string): Promise<number> {
    const organization = this.get((state) => state.organization);
    if (!organization) {
      return 0;
    }
    return this.affiliateRepository.count(organization.id, searchTerm);
  }

  protected async deleteRepositoryItem(itemId: string): Promise<boolean> {
    try {
      const user = this.get((state) => state.items.entities[itemId]);
      const organization = this.get((state) => state.organization);

      if (!user || !organization) {
        return false;
      }

      // First, fetch user's memberships in this organization
      const memberships = await this.listUserMembershipsUseCase.execute(
        new ListUserMemberships({ userId: user.id, organizationId: organization.id })
      );

      if (memberships.length > 0) {
        await this.userClient.send(
          new RemoveMemberships({
            membershipIds: memberships.map((membership) => membership.membershipId).filter((id) => id !== undefined),
            batchSize: MAX_CONCURRENCY,
          })
        );
      }

      // Then delete the affiliation
      if (user.affiliationId) {
        await this.affiliateRepository.delete(user.affiliationId);
      }

      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      return false;
    }
  }

  protected listFlow(
    limit: number,
    lastItem: AffiliatedUserData | undefined,
    searchTerm: string | undefined
  ): Observable<AffiliatedUserData[]> {
    const organization = this.get((state) => state.organization);
    if (!organization) {
      return of([]);
    }
    return from(
      Promise.resolve(
        this.userClient.send(
          new ListUsers({
            organizationId: organization.id,
            limit: limit,
            nextToken: lastItem
              ? {
                  affiliationDate: lastItem.affiliationDate,
                  username: lastItem.username,
                  emailVerified: lastItem.emailVerified,
                  avatarUrl: lastItem.avatarUrl,
                  affiliationId: lastItem.affiliationId,
                  id: lastItem.id ?? '',
                }
              : undefined,
            filters: searchTerm ? { username: searchTerm } : undefined,
          })
        )
      )
    );
  }

  private retrieveRoles(userId$: Observable<string>): Observable<{ roles: RoleData[] }> {
    return userId$.pipe(
      mergeMap((userId) => {
        return from(this.userClient.send(new GetUserRoles({ userId }))).pipe(
          map((roles) => ({ roles: roles.map((r) => ({ ...r, available: false })) })),
          this.handleError()
        );
      }, MAX_CONCURRENCY)
    );
  }

  private getUserWorkspaceCount(userId$: Observable<string>): Observable<{ numWorkspaces: number }> {
    return userId$.pipe(
      concatLatestFrom(() => this.organization$),
      filter(([, organization]) => organization !== undefined),
      mergeMap(([userId, organization]) => {
        return from(this.workspaceRepository.countUserMemberships(userId, organization?.id ?? '')).pipe(
          map((numWorkspaces) => ({ numWorkspaces }))
        );
      }, MAX_CONCURRENCY)
    );
  }

  protected processDeferredAttributes(itemId: string): Observable<Partial<AffiliatedUserData>> {
    const roles$ = this.retrieveRoles(of(itemId));
    const workspaceCount$ = this.getUserWorkspaceCount(of(itemId));
    return merge(roles$, workspaceCount$);
  }

  public get userSelectionCompleted$(): Observable<AffiliatedUserData> {
    return this.selectUserSucceeded$.asObservable();
  }

  public get updateUserTriggered$(): Observable<void> {
    return this.updateUserTrigger$.asObservable();
  }

  private setUserError(message: string) {
    this.setError(message);
  }

  private handleError<T>() {
    return catchError<T, Observable<T>>((error: Error) => {
      console.error(error);
      this.setUserError(error.message);
      return EMPTY;
    });
  }

  // Initialize cascading effects
  private initializeCascadingEffects() {
    this.onSelectUserSucceeded(this.selectUserSucceeded$);
    this.onUpdateUser(this.updateUserTrigger$);
    this.checkOrganizationLimit(this.mustCheckOrganizationLimit$);
    this.listOrganizationUsers(this.organization$);
  }

  private async getUser(userId: string): Promise<AffiliatedUserData> {
    const user = await this.userRepository.getById(userId);
    const roles = await this.rolesRepository.getUserRoles(userId);
    return {
      id: user.id,
      affiliationId: undefined,
      username: user.username,
      emailVerified: user.verified,
      avatarUrl: undefined,
      affiliationDate: user.createdAt,
      roles: roles.map((role) => ({
        id: role?.id ?? '',
        name: role.name,
        available: false,
      })),
    };
  }

  ngOnDestroy(): void {
    // Complete all subjects to prevent memory leaks
    this.selectUserSucceeded$.complete();
    this.updateUserTrigger$.complete();
  }
}
